import { useOrganization } from "@clerk/react";
import { useMemo } from "react";

const COOKIE_NAME = "user_is_admin";

const getCachedRole = (): boolean => {
  if (typeof document === "undefined") return false;
  const cookie = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${COOKIE_NAME}=`));
  return cookie?.split("=")[1] === "true";
};

const setCookie = (value: boolean) => {
  document.cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${
    60 * 60 * 24 * 7
  }`;
};

/**
 * Compliance reviewers (Clerk org role key "compliance") get exactly one
 * surface: the Tasks page, filtered to phase-review rows. This is a DENY
 * role, so unlike useAdminRole there is no cookie fallback — callers gate
 * on isLoaded instead of briefly granting the full UI while Clerk loads.
 * The backend enforces the same rule independently (wsgi.py allowlist).
 */
export const useComplianceRole = () => {
  const { membership, isLoaded } = useOrganization();
  const isCompliance = membership?.role === "org:compliance";
  return { isCompliance, isLoaded };
};

export const useAdminRole = () => {
  const { membership } = useOrganization();

  // Immediately compute admin status - use membership if available, fallback to cookie
  const isAdmin = useMemo(() => {
    if (membership?.role) {
      const currentIsAdmin = membership.role.includes("admin");

      // Update cookie if different from cached
      const cachedValue = getCachedRole();
      if (currentIsAdmin !== cachedValue) {
        setCookie(currentIsAdmin);
      }

      return currentIsAdmin;
    }

    // Fallback to cookie while membership loads
    return getCachedRole();
  }, [membership?.role]);

  return { isAdmin };
};
